import { Handle, Position } from 'reactflow';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Zap,
  Tag as TagIcon,
  UserPlus,
  Webhook,
  CalendarClock,
  PlayCircle,
  AlertCircle,
} from 'lucide-react';

const TRIGGER_DEFS: Record<string, { label: string; icon: any; describe: (data: any) => string }> = {
  manual: {
    label: 'Manual / Campanha',
    icon: PlayCircle,
    describe: () => 'Disparado por você (CRM, campanha, botão).',
  },
  tag_added: {
    label: 'Tag adicionada',
    icon: TagIcon,
    describe: (d) => (d.triggerValue ? `Quando a tag "${d.triggerValue}" é aplicada` : '⚠ Defina a tag'),
  },
  new_contact: {
    label: 'Novo contato',
    icon: UserPlus,
    describe: () => 'Quando um novo contato é criado',
  },
  webhook: {
    label: 'Webhook',
    icon: Webhook,
    describe: (d) => (d.triggerValue ? `Endpoint: ${d.triggerValue}` : 'Endpoint gerado ao salvar'),
  },
  scheduled: {
    label: 'Agendado',
    icon: CalendarClock,
    describe: (d) => (d.triggerValue ? `Em ${d.triggerValue}` : '⚠ Defina data/hora'),
  },
};

export default function TriggerNode({ data }: any) {
  const triggerType = (data.triggerType ?? 'manual') as keyof typeof TRIGGER_DEFS;
  const def = TRIGGER_DEFS[triggerType] ?? TRIGGER_DEFS.manual;
  const Icon = def.icon;
  const describe = def.describe(data);
  const incomplete = describe.startsWith('⚠');

  return (
    <Card
      className={
        'min-w-[260px] max-w-[300px] shadow-md border-2 ' +
        (incomplete ? 'bg-amber-50 border-amber-300' : 'bg-indigo-50 border-indigo-300')
      }
    >
      <div className="p-3">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="h-7 w-7 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0">
            <Zap className="w-4 h-4 text-white" strokeWidth={2.4} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">Gatilho</div>
            <div className="text-sm font-semibold text-indigo-900 truncate">
              {data.label || def.label}
            </div>
          </div>
          {incomplete ? (
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
          ) : (
            <Badge variant="default" className="bg-indigo-600 text-[10px]">início</Badge>
          )}
        </div>

        <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-white/60 border border-indigo-200/60 text-xs">
          <Icon className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
          <span className={incomplete ? 'text-amber-700' : 'text-indigo-700'}>{describe}</span>
        </div>

        <p className="text-[10px] text-indigo-600/70 mt-1.5 leading-snug">
          Tudo que vier abaixo terá acesso ao contato e dados deste gatilho.
        </p>
      </div>
      {/* Trigger nunca tem entrada — só saída. */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !bg-indigo-500 !border-indigo-700"
      />
    </Card>
  );
}
